import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  Settings,
  Save,
  ArrowLeft,
  Loader2,
  Type,
  FileText,
  Tag,
  Plus,
  X,
  Upload,
  Download,
  Sparkles
} from 'lucide-react'
import { dataInputsService } from '@/services/dataInputs'
import { apiRequest } from '@/services/api'
import { ROUTES } from '@/utils/constants'
import toast from 'react-hot-toast'

const DATA_TYPES = [
  { value: 'Text', label: 'Text', description: 'Plain text input' },
  { value: 'Number', label: 'Number', description: 'Whole numbers only' },
  { value: 'Decimal', label: 'Decimal', description: 'Numbers with decimal places' },
  { value: 'Boolean', label: 'Boolean', description: 'True/False checkbox' },
  { value: 'Date', label: 'Date', description: 'Date picker (YYYY-MM-DD)' },
  { value: 'Datetime', label: 'Datetime', description: 'Date and time picker' },
  { value: 'Email', label: 'Email', description: 'Email address validation' },
  { value: 'URL', label: 'URL', description: 'Web URL validation' },
  { value: 'Phone Number', label: 'Phone Number', description: 'Phone number format' },
  { value: 'File', label: 'File', description: 'Any file type (.pdf, .doc, .txt, etc.)' },
  { value: 'Image', label: 'Image', description: 'Image files (.jpg, .png, .gif, .webp)' },
  { value: 'Video', label: 'Video', description: 'Video files (.mp4, .avi, .mov, .webm)' },
  { value: 'Audio', label: 'Audio', description: 'Audio files (.mp3, .wav, .ogg, .m4a)' },
  { value: 'Document', label: 'Document', description: 'Document files (.pdf, .doc, .docx, .txt)' },
  { value: 'Archive', label: 'Archive', description: 'Archive files (.zip, .rar, .7z, .tar)' }
]

const AUTO_GENERATION_TYPES = [
  { value: 'UUID', label: 'UUID', description: 'Universally unique identifier (random)' },
  { value: 'UUIDv4', label: 'UUIDv4', description: 'Random UUID version 4' },
  { value: 'Sequential Number', label: 'Sequential Number', description: 'Auto-incrementing number' },
  { value: 'Timestamp', label: 'Timestamp', description: 'Current timestamp' },
  { value: 'Custom Prefix', label: 'Custom Prefix', description: 'Prefix with sequential number' }
]

const AttributeCreate = () => {
  const { dataInputId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm()

  const watchedDataType = watch('data_type')
  const watchedIsAutoGenerated = watch('is_auto_generated')
  const watchedAutoGenType = watch('auto_generation_type')

  // State for managing value options
  const [valueOptions, setValueOptions] = useState([''])
  const [newOption, setNewOption] = useState('')
  const [isUploadingTxt, setIsUploadingTxt] = useState(false)

  // Functions to manage value options
  const addValueOption = () => {
    if (newOption.trim() && !valueOptions.includes(newOption.trim())) {
      setValueOptions([...valueOptions.filter(opt => opt.trim()), newOption.trim()])
      setNewOption('')
    }
  }

  const removeValueOption = (index) => {
    setValueOptions(valueOptions.filter((_, i) => i !== index))
  }

  const updateValueOption = (index, value) => {
    const updated = [...valueOptions]
    updated[index] = value
    setValueOptions(updated)
  }

  // Function to handle txt file upload for value options
  const handleTxtFileUpload = async (file) => {
    if (!file) return

    setIsUploadingTxt(true)
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await apiRequest.post(
        `/data-inputs/${dataInputId}/attributes/import-value-options`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      const { value_options } = response.data
      if (value_options && value_options.length > 0) {
        setValueOptions(value_options)
        toast.success(`Imported ${value_options.length} options from ${file.name}`)
      } else {
        toast.warning('No valid options found in the file')
      }
    } catch (error) {
      console.error('Error uploading txt file:', error)
      toast.error('Failed to import options from file')
    } finally {
      setIsUploadingTxt(false)
    }
  }

  const {
    data: dataInput,
    isLoading: dataInputLoading
  } = useQuery(
    ['dataInput', dataInputId],
    () => dataInputsService.getById(dataInputId),
    {
      onError: (error) => {
        toast.error('Failed to load data input details')
        console.error('Error loading data input:', error)
      }
    }
  )

  // Create attribute mutation
  const createAttributeMutation = useMutation(
    (attributeData) => dataInputsService.createAttribute(dataInputId, attributeData),
    {
      onSuccess: (newAttribute) => {
        toast.success('Attribute created successfully!')
        queryClient.invalidateQueries(['dataInput', dataInputId])
        navigate(`/data-inputs/${dataInputId}?tab=attributes`)
      },
      onError: (error) => {
        // Don't show generic error toast since API interceptor already shows specific error
        console.error('Error creating attribute:', error)
      }
    }
  )

  const onSubmit = async (data) => {
    // Include value options in the submission
    const filteredOptions = valueOptions.filter(opt => opt.trim())
    
    // Build auto-generation rules if enabled
    let autoGenerationRules = null
    if (data.is_auto_generated && data.auto_generation_type) {
      autoGenerationRules = {
        type: data.auto_generation_type,
        prefix: data.auto_generation_prefix || '',
        padding: parseInt(data.auto_generation_padding) || 4,
        start_from: parseInt(data.auto_generation_start_from) || 1,
        separator: data.auto_generation_separator || '-',
        format: data.auto_generation_format || 'iso'
      }
    }
    
    // Convert checkbox values to boolean (they come as "on" or undefined)
    const convertToBoolean = (value) => value === true || value === 'on'
    
    const attributeData = {
      ...data,
      is_required: convertToBoolean(data.is_required),
      is_active: convertToBoolean(data.is_active),
      is_unique: convertToBoolean(data.is_unique),
      is_auto_generated: convertToBoolean(data.is_auto_generated),
      allowed_values: filteredOptions.length > 0 ? filteredOptions : null,
      auto_generation_rules: autoGenerationRules
    }
    createAttributeMutation.mutate(attributeData)
  }

  if (dataInputLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 mr-2" />
          <span className="text-gray-500">Loading data input details...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/data-inputs/${dataInputId}?tab=attributes`)}
            className="btn-outline btn-sm flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Data Input
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create Attribute</h1>
            <p className="mt-1 text-sm text-gray-600">
              Add a new attribute to {dataInput?.name || 'Data Input'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Attribute Information</h3>
            <p className="mt-1 text-sm text-gray-600">
              Define the basic properties of the attribute
            </p>
          </div>
          <div className="p-6 space-y-6">
            {/* Attribute Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Attribute Name *
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Settings className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('name', {
                    required: 'Attribute name is required',
                    minLength: {
                      value: 2,
                      message: 'Attribute name must be at least 2 characters',
                    },
                    maxLength: {
                      value: 100,
                      message: 'Attribute name must be less than 100 characters',
                    },
                    pattern: {
                      value: /^[a-zA-Z][a-zA-Z0-9_]*$/,
                      message: 'Attribute name must start with a letter and contain only letters, numbers, and underscores',
                    },
                  })}
                  type="text"
                  className={`input pl-10 ${
                    errors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                  }`}
                  placeholder="Enter attribute name (e.g., first_name, email)"
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Label */}
            <div>
              <label htmlFor="label" className="block text-sm font-medium text-gray-700">
                Display Label
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Tag className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('label', {
                    maxLength: {
                      value: 255,
                      message: 'Label must be less than 255 characters',
                    },
                  })}
                  type="text"
                  className={`input pl-10 ${
                    errors.label ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                  }`}
                  placeholder="Enter display label (e.g., First Name, Email Address)"
                />
              </div>
              {errors.label && (
                <p className="mt-1 text-sm text-red-600">{errors.label.message}</p>
              )}
            </div>

            {/* Data Type */}
            <div>
              <label htmlFor="data_type" className="block text-sm font-medium text-gray-700">
                Data Type *
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Type className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  {...register('data_type', {
                    required: 'Data type is required',
                  })}
                  className={`input pl-10 ${
                    errors.data_type ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                  }`}
                >
                  <option value="">Select data type</option>
                  {DATA_TYPES.map((type) => (
                    <option key={type.value} value={type.value} title={type.description}>
                      {type.label} - {type.description}
                    </option>
                  ))}
                </select>
              </div>
              {errors.data_type && (
                <p className="mt-1 text-sm text-red-600">{errors.data_type.message}</p>
              )}

              {/* Data Type Validation Info */}
              {watchedDataType === 'Phone Number' && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-blue-800">Phone Number Validation Rules</h4>
                      <div className="mt-1 text-sm text-blue-700">
                        <ul className="list-disc list-inside space-y-1">
                          <li>Must start with 0</li>
                          <li>Minimum 8 digits required</li>
                          <li>Maximum 15 digits allowed</li>
                          <li>Example: 0123456789</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {watchedDataType === 'Email' && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-blue-800">Email Validation</h4>
                      <div className="mt-1 text-sm text-blue-700">
                        Must be a valid email format (e.g., user@domain.com)
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <div className="mt-1 relative">
                <div className="absolute top-3 left-3 pointer-events-none">
                  <FileText className="h-5 w-5 text-gray-400" />
                </div>
                <textarea
                  {...register('description', {
                    maxLength: {
                      value: 255,
                      message: 'Description must be less than 255 characters',
                    },
                  })}
                  rows={3}
                  className={`input pl-10 resize-none ${
                    errors.description ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                  }`}
                  placeholder="Enter attribute description"
                />
              </div>
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>

            {/* Value Options */}
            <div>
              <label htmlFor="default_value" className="block text-sm font-medium text-gray-700 mb-2">
                Value Options
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Define predefined options that users can select from. Leave empty to allow free text input.
              </p>

              {/* Import from txt file */}
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center mb-2">
                  <Upload className="h-4 w-4 text-blue-600 mr-2" />
                  <h4 className="text-sm font-medium text-blue-900">Import Options from Text File</h4>
                </div>
                <p className="text-xs text-blue-700 mb-3">
                  Upload a .txt file with one option per line. This will replace all existing options.
                </p>
                <div className="flex items-center space-x-2">
                  <input
                    type="file"
                    accept=".txt"
                    onChange={(e) => {
                      const file = e.target.files[0]
                      if (file) {
                        handleTxtFileUpload(file)
                        e.target.value = '' // Reset input
                      }
                    }}
                    disabled={isUploadingTxt}
                    className="text-xs file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200 disabled:opacity-50"
                  />
                  {isUploadingTxt && (
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  )}
                </div>
                <div className="mt-2 flex items-center text-xs text-blue-600">
                  <Download className="h-3 w-3 mr-1" />
                  <button
                    type="button"
                    onClick={() => {
                      const sampleContent = "Option 1\nOption 2\nOption 3\nRed\nBlue\nGreen\nSmall\nMedium\nLarge"
                      const blob = new Blob([sampleContent], { type: 'text/plain' })
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = 'sample_value_options.txt'
                      document.body.appendChild(a)
                      a.click()
                      document.body.removeChild(a)
                      URL.revokeObjectURL(url)
                    }}
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Download sample format
                  </button>
                  <span className="ml-1">: Each line becomes an option</span>
                </div>
              </div>

              <div className="space-y-2">
                {valueOptions.map((option, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => updateValueOption(index, e.target.value)}
                      className="input flex-1"
                      placeholder={`Option ${index + 1}`}
                    />
                    {valueOptions.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeValueOption(index)}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                        title="Remove option"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}

                {/* Add new option */}
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    className="input flex-1"
                    placeholder="Add new option..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addValueOption()
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={addValueOption}
                    disabled={!newOption.trim()}
                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Add option"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <p className="mt-2 text-xs text-gray-500">
                Press Enter or click + to add an option manually, or upload a .txt file to import multiple options at once. Users will see these as dropdown choices when creating records.
              </p>
            </div>

            {/* Format Field for Date/Datetime */}
            {(watchedDataType === 'Date' || watchedDataType === 'Datetime') && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Format <span className="text-red-500">*</span>
                </label>
                <select
                  {...register('format', {
                    required: watchedDataType === 'Date' || watchedDataType === 'Datetime'
                      ? 'Format is required for Date/Datetime fields'
                      : false
                  })}
                  className={`input ${
                    errors.format ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                  }`}
                >
                  <option value="">Select format</option>
                  {watchedDataType === 'Date' && (
                    <>
                      <option value="YYYY-MM-DD">YYYY-MM-DD (2024-01-15)</option>
                      <option value="MM/DD/YYYY">MM/DD/YYYY (01/15/2024)</option>
                      <option value="DD/MM/YYYY">DD/MM/YYYY (15/01/2024)</option>
                      <option value="DD-MM-YYYY">DD-MM-YYYY (15-01-2024)</option>
                      <option value="MMM DD, YYYY">MMM DD, YYYY (Jan 15, 2024)</option>
                      <option value="MMMM DD, YYYY">MMMM DD, YYYY (January 15, 2024)</option>
                    </>
                  )}
                  {watchedDataType === 'Datetime' && (
                    <>
                      <option value="YYYY-MM-DD HH:mm:ss">YYYY-MM-DD HH:mm:ss (2024-01-15 14:30:00)</option>
                      <option value="YYYY-MM-DD HH:mm">YYYY-MM-DD HH:mm (2024-01-15 14:30)</option>
                      <option value="MM/DD/YYYY HH:mm:ss">MM/DD/YYYY HH:mm:ss (01/15/2024 14:30:00)</option>
                      <option value="MM/DD/YYYY HH:mm">MM/DD/YYYY HH:mm (01/15/2024 14:30)</option>
                      <option value="DD/MM/YYYY HH:mm:ss">DD/MM/YYYY HH:mm:ss (15/01/2024 14:30:00)</option>
                      <option value="DD/MM/YYYY HH:mm">DD/MM/YYYY HH:mm (15/01/2024 14:30)</option>
                      <option value="MMM DD, YYYY HH:mm">MMM DD, YYYY HH:mm (Jan 15, 2024 14:30)</option>
                      <option value="MMMM DD, YYYY HH:mm:ss">MMMM DD, YYYY HH:mm:ss (January 15, 2024 14:30:00)</option>
                    </>
                  )}
                </select>
                {errors.format && (
                  <p className="mt-1 text-sm text-red-600">{errors.format.message}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  This format will be used for displaying and validating {watchedDataType.toLowerCase()} values
                </p>
              </div>
            )}

            {/* Options */}
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  {...register('is_required')}
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Required Field
                </label>
              </div>

              <div className="flex items-center">
                <input
                  {...register('is_unique')}
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Unique Values Only
                </label>
              </div>

              <div className="flex items-center">
                <input
                  {...register('is_active')}
                  type="checkbox"
                  defaultChecked={true}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Active Attribute
                </label>
              </div>
            </div>

            {/* Auto-Generated Section */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center mb-4">
                <Sparkles className="h-4 w-4 text-gray-600 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">Auto-Generated Value</h3>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center">
                  <input
                    {...register('is_auto_generated')}
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700 font-medium">
                    Enable Auto-Generated Value
                  </label>
                </div>
                <p className="mt-1 text-xs text-blue-500 ml-6">
                  Auto-generated values will be automatically created when a record is added. 
                  Users won't need to manually enter this value.
                </p>
              </div>

              {watchedIsAutoGenerated && (
                <div className="space-y-4 pl-2">
                  {/* Auto-Generation Type */}
                  <div>
                    <label htmlFor="auto_generation_type" className="block text-sm font-medium text-gray-700">
                      Generation Type *
                    </label>
                    <select
                      {...register('auto_generation_type', {
                        required: watchedIsAutoGenerated ? 'Generation type is required' : false
                      })}
                      className={`input mt-1 ${
                        errors.auto_generation_type ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                      }`}
                    >
                      <option value="">Select generation type</option>
                      {AUTO_GENERATION_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label} - {type.description}
                        </option>
                      ))}
                    </select>
                    {errors.auto_generation_type && (
                      <p className="mt-1 text-sm text-red-600">{errors.auto_generation_type.message}</p>
                    )}
                  </div>

                  {/* Prefix for Sequential/Custom */}
                  {(watchedAutoGenType === 'Sequential Number' || watchedAutoGenType === 'Custom Prefix') && (
                    <div>
                      <label htmlFor="auto_generation_prefix" className="block text-sm font-medium text-gray-700">
                        Prefix
                      </label>
                      <input
                        {...register('auto_generation_prefix')}
                        type="text"
                        className="input mt-1"
                        placeholder="e.g., INV-, REF-, ID-"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Optional prefix to add before the number (e.g., "INV-0001")
                      </p>
                    </div>
                  )}

                  {/* Separator for Custom Prefix */}
                  {watchedAutoGenType === 'Custom Prefix' && (
                    <div>
                      <label htmlFor="auto_generation_separator" className="block text-sm font-medium text-gray-700">
                        Separator
                      </label>
                      <input
                        {...register('auto_generation_separator')}
                        type="text"
                        className="input mt-1"
                        placeholder="-"
                        defaultValue="-"
                      />
                    </div>
                  )}

                  {/* Padding for Sequential/Custom */}
                  {(watchedAutoGenType === 'Sequential Number' || watchedAutoGenType === 'Custom Prefix') && (
                    <div>
                      <label htmlFor="auto_generation_padding" className="block text-sm font-medium text-gray-700">
                        Padding (Number of Digits)
                      </label>
                      <input
                        {...register('auto_generation_padding')}
                        type="number"
                        className="input mt-1"
                        placeholder="4"
                        defaultValue="4"
                        min="1"
                        max="10"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Number of digits to pad (e.g., 4 = 0001, 6 = 000001)
                      </p>
                    </div>
                  )}

                  {/* Start From for Sequential/Custom */}
                  {(watchedAutoGenType === 'Sequential Number' || watchedAutoGenType === 'Custom Prefix') && (
                    <div>
                      <label htmlFor="auto_generation_start_from" className="block text-sm font-medium text-gray-700">
                        Start From
                      </label>
                      <input
                        {...register('auto_generation_start_from')}
                        type="number"
                        className="input mt-1"
                        placeholder="1"
                        defaultValue="1"
                        min="1"
                      />
                    </div>
                  )}

                  {/* Format for Timestamp */}
                  {watchedAutoGenType === 'Timestamp' && (
                    <div>
                      <label htmlFor="auto_generation_format" className="block text-sm font-medium text-gray-700">
                        Timestamp Format
                      </label>
                      <select
                        {...register('auto_generation_format')}
                        className="input mt-1"
                      >
                        <option value="iso">ISO Format (2024-01-15T10:30:00)</option>
                        <option value="unix">Unix Timestamp (1705315800)</option>
                        <option value="YYYY-MM-DD">Date Only (2024-01-15)</option>
                      </select>
                    </div>
                  )}

                  {/* Auto-generated attributes should be unique */}
                  <div className="flex items-center bg-blue-50 border border-blue-200 rounded p-3">
                    <input
                      {...register('is_unique')}
                      type="checkbox"
                      defaultChecked={true}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 text-sm text-blue-700">
                      Unique Values Only (recommended for auto-generated)
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/data-inputs/${dataInputId}?tab=attributes`)}
            className="btn-outline btn-md"
            disabled={createAttributeMutation.isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary btn-md flex items-center"
            disabled={createAttributeMutation.isLoading}
          >
            {createAttributeMutation.isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Creating...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Create Attribute
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default AttributeCreate
